import React, { useState } from 'react';
import { ArrayFields, Button, Card, FormattedMessageDiv, FormInput, FormWrapper, LoadingAnimation, scrubSentinels, useWizardContext } from '@odmonorepo/core-components';
import { formatDateInput, validateDateComplete, validateDateFormat, formatToISODate, formatFromISODate, validateNotFuture } from '@odmonorepo/odfunctions';
import { Stack } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { patchAppDataOpts, useApplication } from 'merchant-api/unified';
import { Application, Person } from 'merchant-api/types';
import * as rfc6902 from 'rfc6902';
import { ensureSingleItem } from '../businessFormHelpers';
import { AddressInputSchema, AddressInput } from './AddressInput';
import { useMutation } from '@tanstack/react-query';
import { isValidSSN } from 'merchant-components';

const SSN = 'SOCIAL_SECURITY_NUMBER';

const formatMaskedSSN = (value: string) => {
  const cleaned = value.replace(/[^0-9x*]/gi, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
};

export const OwnerContactSchema = (showName: boolean, originalSSN?: string) =>
  yup.object({
    ...(showName && {
      firstName: yup.string().required(),
      lastName: yup.string().required(),
    }),
    addresses: yup.array().of(AddressInputSchema),
    dateOfBirth: yup.string()
      .required('formInput.dateOfBirth.error')
      .test('must be complete date', 'formInput.dateOfBirth.error', validateDateComplete)
      .test('must be valid date', 'formInput.dateOfBirth.error.invalidFormat', validateDateFormat)
      .test('must be in past', 'formInput.dateOfBirth.error.futureDate', validateNotFuture),
    identifiers: yup.array().of(
      yup.object({
        value: yup.string().when('type', {
          is: SSN,
          then: (schema) =>
            schema.required().test('ssn-validation', 'Social Security Number is invalid', function (value) {
              if (!value) return false;
              if (value === originalSSN) return true;
              if (/x/i.test(value)) return false;
              return isValidSSN(value);
            }),
          otherwise: (schema) => schema,
        }),
      }),
    ),
  });

const formatData = (data: Application) => {
  const formattedData = JSON.parse(JSON.stringify(data.data));
  formattedData.party.persons = ensureSingleItem(formattedData?.party?.persons, { identifiers: [{ type: SSN }], addresses: [{ type: 'PHYSICAL_ADDRESS' }] });

  if (formattedData.party.persons[0].dateOfBirth) {
    formattedData.party.persons[0].dateOfBirth = formatFromISODate(formattedData.party.persons[0].dateOfBirth) || '';
  } else {
    formattedData.party.persons[0].dateOfBirth = '';
  }

  formattedData.party.persons[0].identifiers = formattedData.party.persons[0].identifiers || [];
  formattedData.party.persons[0].addresses = formattedData.party.persons[0].addresses || [];

  let ssnIndex = formattedData.party.persons[0].identifiers?.findIndex((item) => item.type === SSN);
  if (ssnIndex === -1) {
    formattedData.party.persons[0].identifiers.push({
      type: SSN,
      value: ''
    });
    ssnIndex = formattedData.party.persons[0].identifiers.length - 1;
  }

  if (!formattedData.party.persons[0].addresses.some((item) => item.type === 'PHYSICAL_ADDRESS')) {
    formattedData.party.persons[0].addresses.push({
      type: 'PHYSICAL_ADDRESS',
      lineOne: '',
      lineTwo: '',
      city: '',
      state: '',
      postalCode: ''
    });
  }

  return {
    defaultValues: scrubSentinels({ ...formattedData.party.persons[0] }),
    ssnIndex,
  };
};

export const OwnerReview: React.FC = () => {
  const { applicationData } = useApplication();

  if (!applicationData) return <Stack className="m-auto"><LoadingAnimation/></Stack>;
  const { ssnIndex, defaultValues } = formatData(applicationData);
  const originalSSN = applicationData?.data?.party?.persons?.[0]?.identifiers?.find((id) => id.type === SSN)?.value;

  return (
    <FormWrapper schema={OwnerContactSchema(true, originalSSN)} defaultValues={defaultValues}>
      <OwnerForm applicationData={applicationData} ssnIndex={ssnIndex} />
    </FormWrapper>
  );
};

type OwnerFormProps = {
  applicationData?: Application;
  ssnIndex: number;
};

const OwnerForm = ({ applicationData, ssnIndex }: OwnerFormProps) => {
  const { getValues, trigger } = useFormContext();
  const { nextStep, previousStep, isFirstStep } = useWizardContext();
  const isOwnerStepFirst = isFirstStep();
  const [submitting, setSubmitting] = useState(false);

  const { mutateAsync: patch } = useMutation({
    ...patchAppDataOpts(),
    onSuccess: () => nextStep(),
  });

  const onNext = async () => {
    const updatedPerson = getValues();
    const personsArr = applicationData?.data?.party?.persons ?? [];
    const index = personsArr.findIndex((person) => person.id === updatedPerson.id);
    const personIndex = index >= 0 ? index : 0;
    const output = {
      ...applicationData?.data,
      party: {
        ...applicationData?.data?.party,
        persons: [...personsArr.slice(0, personIndex), updatedPerson, ...personsArr.slice(personIndex + 1)],
      },
    };
    if (updatedPerson?.dateOfBirth) {
      output.party.persons[personIndex].dateOfBirth = formatToISODate(updatedPerson.dateOfBirth);
    }
    const patches = rfc6902.createPatch(applicationData?.data, output);
    await patch({ id: applicationData!.id, patches });
  };

  const handleContinue = async () => {
    if (submitting) return;

    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    try {
      await onNext();
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (isOwnerStepFirst) {
      window.history.back();
      return;
    }

    previousStep();
  };

  return (
    <Stack gap={5} className="w-100 mx-auto px-2 px-md-0" style={{ maxWidth: '600px' }}>
      <FormattedMessageDiv className="h3 text-center mb-0" id="ownerReview.title" />
      <Card key={0} className="border-0 shadow-sm" bodyClassName="p-5">
        <Stack gap={5}>
          <Stack gap={1}>
            <FormattedMessageDiv className="h4 mb-0" id="ownerReview.contactInfo" />
            <FormattedMessageDiv className="fs-6" id="ownerReview.contactInfo.description" />
          </Stack>
          <ContactInfo ssnIndex={ssnIndex} />
          <Stack gap={4} className="mt-2">
            <Button onClick={handleContinue} loading={submitting} disabled={submitting} style={{ width: '100%' }}>
              Continue
            </Button>
            <Button variant="outline-primary" onClick={handleBack} disabled={submitting} style={{ width: '100%' }}>
              Back
            </Button>
          </Stack>
        </Stack>
      </Card>
      <FormattedMessageDiv className="fs-8" id="ownerInfo.disclaimer" values={{ sup: (text: string) => <sup>{text}</sup> }} />
    </Stack>
  );
};

const ContactInfo = ({ ssnIndex }: { ssnIndex: number }) => (
  <Stack gap={5}>
    <FormInput<Person> name="firstName" data-testid="firstName" controlProps={{ autoComplete: 'given-name' }} />
    <FormInput<Person> name="lastName" data-testid="lastName" controlProps={{ autoComplete: 'family-name' }} />
    <AddressInput<Person> compactStateZip />
    <FormInput<Person>
      name="dateOfBirth"
      data-testid="dateOfBirth"
      formatValue={formatDateInput}
      label={<FormattedMessageDiv id="formInput.dateOfBirth" />}
      helperText={<FormattedMessageDiv id="formInput.dateOfBirth.helperText" />}
      useYupErrors={true}
      controlProps={{ inputMode: 'numeric' }}
    />
    <SSNInfo ssnIndex={ssnIndex} />
  </Stack>
);

const SSNInfo = ({ ssnIndex }: { ssnIndex: number }) => (
  <ArrayFields<Person> name="identifiers" data-testid="identifiers">
    {() => (
      <FormInput<Person>
        className="fs-mask"
        name={`identifiers.${ssnIndex}.value`}
        data-testid="ssn"
        stripFormatOnSave
        formatValue={formatMaskedSSN}
        label={<FormattedMessageDiv id="formInput.ssn" />}
        helperText={
          <FormattedMessageDiv
            id="ownerReview.ssn.description"
            values={{
              b: (text: React.ReactNode) => <span className="fw-semibold">{text}</span>,
              sup: (text: string) => <sup>{text}</sup>,
            }}
          />
        }
        controlProps={{
          inputMode: 'numeric',
          onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
            setTimeout(() => e.target.select(), 0);
          },
          onClick: (e: React.MouseEvent<HTMLInputElement>) => {
            (e.target as HTMLInputElement).select();
          },
        }}
      />
    )}
  </ArrayFields>
);

export default OwnerReview;
